import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Phone, Shield, ArrowRight, Leaf, KeyRound } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const clean = phone.replace(/\D/g, '');
    if (clean.length < 10) {
      setError('Enter a valid 10-digit phone number');
      return;
    }
    setError('');
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/auth/send-otp`, { phone: clean }, { withCredentials: true });
      setStep('otp');
      // Start 30s resend countdown
      setCountdown(30);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      setError('Enter the OTP sent to your phone');
      return;
    }
    setError('');
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-otp`, {
        phone: phone.replace(/\D/g, ''),
        otp: otp.trim()
      }, { withCredentials: true });
      await checkAuth();
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setError('');
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/auth/send-otp`, { phone: phone.replace(/\D/g, '') }, { withCredentials: true });
      setCountdown(30);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F7F8F6' }} data-testid="login-page">
      {/* Left — Brand panel */}
      <div className="hidden lg:flex lg:w-[45%] relative items-center justify-center p-12" style={{ backgroundColor: '#063D2F' }}>
        <div className="max-w-sm animate-fade-in-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-[14px] flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Leaf className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-heading font-extrabold text-white text-lg tracking-[3px] uppercase">EDEN</h1>
              <p className="text-[9px] font-bold uppercase tracking-[1.2px]" style={{ color: 'rgba(255,255,255,0.45)' }}>Skin Clinic</p>
            </div>
          </div>
          <h2 className="font-heading text-4xl font-extrabold text-white leading-tight mb-4" style={{ letterSpacing: '-0.5px' }}>
            Precision care, beautifully simple.
          </h2>
          <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
            AI-powered consultations, intelligent prescriptions, and complete patient tracking — unified in one clinical platform.
          </p>
          <div className="flex gap-3 mt-8">
            {['AI Transcription', 'Smart Prescriptions', 'Patient Timeline'].map(f => (
              <span key={f} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" style={{ color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md space-y-6 animate-slide-in-right">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-4">
            <div className="inline-flex items-center gap-2">
              <Leaf className="w-6 h-6" style={{ color: '#0E5132' }} />
              <span className="font-heading font-extrabold text-lg tracking-[3px] uppercase" style={{ color: '#0E5132' }}>EDEN</span>
            </div>
          </div>

          {/* Login card */}
          <div className="bg-white rounded-[20px] p-8 border" style={{ borderColor: '#E7EAE7' }}>
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-extrabold" style={{ color: '#1A2E22', letterSpacing: '-0.5px' }}>
                {step === 'phone' ? 'Welcome back.' : 'Enter OTP'}
              </h2>
              <p className="text-sm mt-1" style={{ color: 'rgba(26,46,34,0.58)' }}>
                {step === 'phone' ? 'Sign in with your registered phone number' : `OTP sent to +91 ${phone.replace(/\D/g, '').slice(-10)}`}
              </p>
            </div>

            {step === 'phone' ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="login-error" className="rounded-[14px]">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[1px]" style={{ color: 'rgba(26,46,34,0.58)' }}>Phone Number</label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 rounded-[14px] h-12 border text-sm font-medium" style={{ backgroundColor: '#F7F8F6', borderColor: 'rgba(14,81,50,0.10)', color: '#0E5132' }}>
                      +91
                    </div>
                    <Input
                      type="tel"
                      placeholder="Enter 10-digit number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      disabled={sending}
                      maxLength={10}
                      className="rounded-[14px] h-12 border focus:ring-0 flex-1"
                      style={{ backgroundColor: '#F7F8F6', borderColor: 'rgba(14,81,50,0.10)' }}
                      data-testid="login-phone-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full rounded-[14px] h-12 font-extrabold text-sm gap-2"
                  style={{ backgroundColor: '#0E5132' }}
                  data-testid="send-otp-button"
                >
                  {sending ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sending OTP...</>
                  ) : (
                    <><Phone className="h-4 w-4" /> Send OTP</>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="login-error" className="rounded-[14px]">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[1px]" style={{ color: 'rgba(26,46,34,0.58)' }}>One-Time Password</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                    disabled={verifying}
                    maxLength={6}
                    autoFocus
                    className="rounded-[14px] h-14 border focus:ring-0 text-center text-2xl tracking-[8px] font-bold"
                    style={{ backgroundColor: '#F7F8F6', borderColor: 'rgba(14,81,50,0.10)', color: '#0E5132' }}
                    data-testid="login-otp-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={verifying}
                  className="w-full rounded-[14px] h-12 font-extrabold text-sm gap-2"
                  style={{ backgroundColor: '#0E5132' }}
                  data-testid="verify-otp-button"
                >
                  {verifying ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Verifying...</>
                  ) : (
                    <><KeyRound className="h-4 w-4" /> Verify &amp; Sign In</>
                  )}
                </Button>

                <div className="flex items-center justify-between text-xs" style={{ color: 'rgba(26,46,34,0.58)' }}>
                  <button
                    type="button"
                    onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
                    className="hover:underline"
                    data-testid="change-phone-button"
                  >
                    Change number
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={countdown > 0 || sending}
                    className={countdown > 0 ? 'opacity-50' : 'hover:underline'}
                    data-testid="resend-otp-button"
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Security note */}
          <div className="flex items-center justify-center gap-2 text-[10px]" style={{ color: 'rgba(26,46,34,0.38)' }}>
            <Shield className="w-3 h-3" />
            <span>Secured by 2-factor OTP authentication</span>
          </div>
        </div>
      </div>
    </div>
  );
}
